import React, { useCallback, useRef, Fragment, use } from "react";

import { isNonEmptyArray } from "_helpers";

import { Button, Checkbox, Col, Drawer, Divider, Form, Input, Row, Select, Space, Typography } from "antd";

const PermissionsInput = ({ tree, value, onChange, ...props }) => {
    const mapTitle = useRef({
        reporting: 'Reporting',
        filestatus: 'Etat des dossiers',
        filetracking: 'Suivi des dossiers',
        dashboard: 'Dashboard',
    }).current;

    const renderNode = ( node, level = 0 ) => {
        const { key, txt, parent, children } = node;
        const hasChildren = isNonEmptyArray(children);
        const { Title } = Typography;

        return (
            <Fragment key={key}>
                {
                    parent === 'root' 
                    && (
                        <Col xs={24} className="ms-2 mt-2">
                            <Title level={5}>
                                { mapTitle[key] || txt }
                            </Title>
                        </Col>
                    )
                }

                <Col 
                    xs={24} 
                    style={{ 
                        marginLeft: (level+1) * 18,
                        marginTop: level > 0 ? 4 : 0
                    }}
                >
                    <Checkbox value={key}>{ txt }</Checkbox>
                </Col>

                {
                    hasChildren && children.map(child => renderNode(child, level + 1))
                }
            </Fragment>
        );
    };

    return (
        <Checkbox.Group 
            value={value}
            onChange={onChange}
            className="d-flex flex-column" 
            style={{ gap: 8 }}
            {...props}
        >
            <Row className="w-100">
                { tree.map(node => renderNode(node)) }
            </Row>
        </Checkbox.Group>
    );
}

const Footer = ({ onClose, onSubmit }) => (
    <div className="d-flex justify-content-end">
        <Space>
            <Button type="primary" onClick={onSubmit}>Sauver</Button>
            <Button type="primary" danger onClick={onClose}>Annuler</Button>
        </Space>
    </div>
)

const requiredMark = ( label, { required }) => (
    <>
        { label }
        { required && <span className="span-red">*</span> }
    </>
)

export const DrawerAddUser = ({
    open,
    permissionsTree,
    setValues,
    onClose
}) => {
    const [ form ] = Form.useForm();
    const { Title, Text } = Typography;

    const roleOptions = useRef([
        { value: "_empty", label: "Sélection", disabled: true },
        { value: "user", label: "Utilisateur" }
    ]).current;

    const mapName = {
        username: "Nom d'utilisateur",
        password: "Mot de passe",
        email: "E-mail",
        role: "Rôle",
        permissions: "Permissions"
    }
    const validateMessages = { 
        required : ( name ) => {
            if( name === 'email' ) return(<>L' <span className="bld-txt">{mapName[name] || name}</span> est requis</>);
            if( name === 'permissions' ) return(<>Les <span className="bld-txt">{mapName[name] || name}</span> sont requises</>);
            return(<>Le <span className="bld-txt">{mapName[name] || name}</span> est requis</>);
        } 
    }

    const handleCancel = useCallback((  ) => {
        form.resetFields();
        onClose();
    }, [form, onClose])

    const handleSubmit = useCallback( async (  ) => {
        const values = form.getFieldsValue();

        try{
            await form.validateFields();
        } catch(err){
            console.log(err);
            return ;
        }
        
        setValues(values, 'add');
        handleCancel();
    }, [form])

    return (
        <Drawer 
            open={open} 
            onClose={handleCancel}
            destroyOnHidden={true}
            styles={{ 
                content: { overflowX: 'hidden' }, 
                header: { display: 'none' },
                body: { overflowX: 'hidden' },
                footer: { border: 'none' }
            }}
            width={480}
            footer={<Footer onClose={handleCancel} onSubmit={handleSubmit} />}
        >
            <Divider style={{ marginBottom: 24 }}>
                <Title level={4} className="mb-0">
                    Ajout d'un utilisateur
                </Title>
            </Divider>
            <Form 
                form={form}
                labelAlign="left"
                labelCol={{ span: 8 }} 
                wrapperCol={{ span: 16 }}
                validateMessages={validateMessages}
                requiredMark={requiredMark}
            >
                <Form.Item 
                    label="Nom d'utilisateur" 
                    name="username" 
                    className="mb-2"
                    rules={[{ required: true }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item 
                    label="Mot de passe" 
                    name="password" 
                    className="mb-2"
                    rules={[{ required: true }]}
                >
                    <Input.Password autoComplete="new-password" />
                </Form.Item>
                <Form.Item 
                    label="E-mail" 
                    name="email" 
                    className="mb-2"
                    rules={[{ required: true }]}
                >
                    <Input autoComplete="email" />
                </Form.Item>
                <Form.Item 
                    label="Rôle" 
                    name="role" 
                    className="mb-2"
                    rules={[{ required: true }]}
                >
                    <Select options={roleOptions} />
                </Form.Item>
                <div className="mt-3">
                    <Form.Item shouldUpdate noStyle>
                        {({ getFieldError }) => {
                            const errors = getFieldError("permissions");
                            return (<>{ isNonEmptyArray(errors) && <div style={{ color: "red" }}>{ errors[0] }</div> } </>);
                        }}
                    </Form.Item>
                    <Text>
                        Permissions<span className="span-red">*</span><span style={{ marginLeft: 2 }}>:</span>
                    </Text>
                    <Form.Item 
                        name="permissions" 
                        layout="vertical" 
                        className="mb-2"
                        labelCol={{ span: 24 }} 
                        wrapperCol={{ span: 24 }}
                        rules={[{ required: true }]}
                        noStyle
                    >
                        <PermissionsInput tree={permissionsTree}/>
                    </Form.Item>
                </div>
            </Form>
        </Drawer>
    );
}